import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

export default function MapExistingDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [u_dise, setUdise] = useState("");
  const [match, setMatch] = useState(null);
  const [checking, setChecking] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) { setUdise(""); setMatch(null); setReason(""); setChecking(false); }
  }, [open]);

  const checkUdise = async () => {
    const u = u_dise.trim();
    if (!u) { toast.error("Enter a U-DISE number"); return; }
    setChecking(true); setMatch(null);
    try {
      const { data } = await api.get(`/crm/udise-check/${encodeURIComponent(u)}`);
      if (!data.exists) { toast.error("No school found with this U-DISE in Client Database"); }
      else setMatch(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setChecking(false);
  };

  const submit = async () => {
    if (!match) return;
    try {
      await api.post("/crm/reassign-requests", {
        client_id: match.client_id,
        new_assignee_id: user.id,
        new_assignee_name: user.name,
        reason,
      });
      toast.success("Mapping request sent to CRM Manager / Admin");
      onOpenChange(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="map-existing-dialog">
        <DialogHeader>
          <DialogTitle className="heading">Request Existing School (by U-DISE)</DialogTitle>
          <div className="text-xs text-slate-500 mt-1">Enter the U-DISE number of a school already in the Client Database. Your request will be sent to the CRM Manager / Master Admin for approval.</div>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">U-DISE Number</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={u_dise} onChange={(e) => setUdise(e.target.value)} placeholder="11-digit U-DISE" data-testid="map-udise" />
              <Button onClick={checkUdise} disabled={checking} className="h-10 bg-slate-900 hover:bg-slate-800 text-white" data-testid="map-check">
                {checking ? "Checking…" : "Lookup"}
              </Button>
            </div>
          </div>

          {match && (
            <div className="border border-emerald-300 bg-emerald-50 rounded-md p-3" data-testid="map-match">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800">School Found</div>
              <div className="text-sm font-semibold text-slate-900 mt-1">{match.client_name}</div>
              <div className="text-[12px] text-slate-700">Currently assigned to <span className="font-semibold">{match.assigned_to_name || "— (unassigned)"}</span></div>
              <div className="mt-3">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Reason for mapping to you</Label>
                <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why should this school be mapped to you?" className="mt-1" data-testid="map-reason" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!match} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="map-submit">
            Send Mapping Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
